import { ref, computed, watch } from 'vue'
import { normalizeModifierUuidList } from '@/models/MenuItemModifier.js'
import { isPublicMenuCartLineValid } from '@/utils/publicMenuCart.js'

const STORAGE_PREFIX = 'qrestoo_cart_'
const MAX_QTY = 99

function storageKey(slug) {
  return `${STORAGE_PREFIX}${slug}`
}

function loadFromStorage(slug) {
  try {
    const raw = localStorage.getItem(storageKey(slug))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveToStorage(slug, items) {
  try {
    localStorage.setItem(storageKey(slug), JSON.stringify(items))
  } catch {
    // localStorage full or unavailable
  }
}

/**
 * Cart composable for the Quick Order feature.
 * Persists cart items in localStorage keyed by restaurant slug.
 *
 * @param {string} slug - Restaurant slug
 */
export function useQuickOrderCart(slug) {
  const items = ref(loadFromStorage(slug))

  watch(items, (val) => saveToStorage(slug, val), { deep: true })

  const itemCount = computed(() =>
    items.value.reduce((sum, i) => sum + (i.quantity ?? 0), 0),
  )

  const isEmpty = computed(() => items.value.length === 0)

  /** Match cart line by menu_item_uuid, variant_uuid, and sorted modifier UUIDs. */
  function sameLine(a, b) {
    const ma = JSON.stringify(normalizeModifierUuidList(a.modifier_uuids))
    const mb = JSON.stringify(normalizeModifierUuidList(b.modifier_uuids))
    return a.menu_item_uuid === b.menu_item_uuid && (a.variant_uuid ?? '') === (b.variant_uuid ?? '') && ma === mb
  }

  function addItem(item) {
    /**
     * When a matching line exists: `replace === true` sets quantity to the payload amount (absolute).
     * `replace === false` adds the payload amount to the existing line (another “Add to cart”).
     * New lines ignore `replace` and use the payload quantity.
     */
    const replace = item.replace === true
    const normalized = {
      menu_item_uuid: item.menu_item_uuid,
      variant_uuid: item.variant_uuid ?? null,
      modifier_uuids: normalizeModifierUuidList(item.modifier_uuids),
      name: item.name ?? '',
      quantity: Math.min(Math.max(item.quantity ?? 1, 1), MAX_QTY),
      price: item.price ?? null,
      image_url: item.image_url ?? null,
    }
    const idx = items.value.findIndex((i) => sameLine(i, normalized))
    if (idx >= 0) {
      const next = [...items.value]
      const prevQty = Math.max(0, Number(next[idx].quantity) || 0)
      const nextQty = replace
        ? normalized.quantity
        : Math.min(prevQty + normalized.quantity, MAX_QTY)
      next[idx] = { ...next[idx], quantity: nextQty }
      items.value = next
    } else {
      items.value = [...items.value, normalized]
    }
  }

  function removeItem(menuItemUuid, variantUuid = null, modifierUuids = null) {
    const modKey = JSON.stringify(normalizeModifierUuidList(modifierUuids))
    items.value = items.value.filter((i) => {
      const sameLine =
        i.menu_item_uuid === menuItemUuid &&
        (i.variant_uuid ?? '') === (variantUuid ?? '') &&
        JSON.stringify(normalizeModifierUuidList(i.modifier_uuids)) === modKey
      return !sameLine
    })
  }

  function updateQuantity(menuItemUuid, quantity, variantUuid = null, modifierUuids = null) {
    if (quantity <= 0) {
      removeItem(menuItemUuid, variantUuid, modifierUuids)
      return
    }
    const clamped = Math.min(quantity, MAX_QTY)
    const modKey = JSON.stringify(normalizeModifierUuidList(modifierUuids))
    const idx = items.value.findIndex(
      (i) =>
        i.menu_item_uuid === menuItemUuid &&
        (i.variant_uuid ?? '') === (variantUuid ?? '') &&
        JSON.stringify(normalizeModifierUuidList(i.modifier_uuids)) === modKey,
    )
    if (idx < 0) return
    const next = [...items.value]
    next[idx] = { ...next[idx], quantity: clamped }
    items.value = next
  }

  function clearCart() {
    items.value = []
  }

  function validateCart(availableItems) {
    if (!Array.isArray(availableItems) || !availableItems.length) return
    const byUuid = new Map()
    for (const item of availableItems) {
      const uuid = item?.uuid ?? item?.menu_item_uuid
      if (uuid) byUuid.set(String(uuid), item)
    }
    const filtered = items.value.filter((line) =>
      isPublicMenuCartLineValid(line, byUuid.get(String(line.menu_item_uuid))),
    )
    if (filtered.length !== items.value.length) {
      items.value = filtered
    }
  }

  function getItemQuantity(menuItemUuid, variantUuid = null, modifierUuids = null) {
    const modKey = JSON.stringify(normalizeModifierUuidList(modifierUuids))
    const item = items.value.find(
      (i) =>
        i.menu_item_uuid === menuItemUuid &&
        (i.variant_uuid ?? '') === (variantUuid ?? '') &&
        JSON.stringify(normalizeModifierUuidList(i.modifier_uuids)) === modKey,
    )
    return item?.quantity ?? 0
  }

  return {
    items,
    itemCount,
    isEmpty,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    validateCart,
    getItemQuantity,
  }
}
